/* [ START OF MODULE - VER 1.4 (src/context/AppDataContext.jsx) ] */
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useRealtime } from './useRealtime';
import { useAuth } from './useAuth';
import { AppDataContext } from './useAppData';
import { ROSTER_COLUMNS } from '../utils/techRosterUtils';

export const AppDataProvider = ({ children }) => {
  const [techRoster, setTechRoster] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Pull tier from AuthContext rather than re-fetching from profiles_v2.
  // AuthContext already loaded the full profile (including tier_level) on
  // login; the previous version of this file did supabase.auth.getUser()
  // plus a second profiles_v2 SELECT for the same row, adding two
  // unnecessary network round-trips to the post-login render path.
  const { profile } = useAuth();
  const userTier = profile?.tier_level || 0;

  const fetchCatalog = useCallback(async () => {
    const { data } = await supabase.from('tecc_templates').select('*').eq('is_active', true).order('name');
    if (data) setCatalogItems(data);
  }, []);

  // RMI-2026-707: reload just the roster (the profile editor calls this after a dispatch-availability change so the pickers update live)
  const refreshRoster = useCallback(async () => {
    const { data, error } = await supabase.from('profiles_v2').select(`${ROSTER_COLUMNS}, profile_type`).eq('status', 'ACTIVE').eq('is_active', true).eq('profile_type', 'internal').order('first_name');
    if (!error) setTechRoster(data || []);
  }, []);

  useEffect(() => {
    const fetchGlobalTelemetry = async () => {
      try {
        // Two parallel queries — no getUser() round-trip, no redundant
        // profile re-fetch. Just the data this context actually owns.
        const [rosterRes, catalogRes] = await Promise.all([
          supabase.from('profiles_v2').select(`${ROSTER_COLUMNS}, profile_type`).eq('status', 'ACTIVE').eq('is_active', true).eq('profile_type', 'internal').order('first_name'),
          supabase.from('tecc_templates').select('*').eq('is_active', true).order('name'),
        ]);

        if (!rosterRes.error) setTechRoster(rosterRes.data || []);
        if (!catalogRes.error) setCatalogItems(catalogRes.data || []);
        setIsDataLoaded(true);

      } catch (err) {
        console.error("Global Cache Mount Failure:", err.message);
        setIsDataLoaded(true);
      }
    };

    fetchGlobalTelemetry();
  }, []);

  // Keep catalog fresh when templates change — requires RealtimeProvider to be a parent
  const { subscribe } = useRealtime();
  useEffect(() => {
    const unsub = subscribe('tecc_templates', () => fetchCatalog());
    return unsub;
  }, [subscribe, fetchCatalog]);

  const value = {
    techRoster,
    refreshRoster,
    catalogItems,
    userTier,
    isDataLoaded
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};
/*
=========================================
CHANGE MANIFEST
=========================================
VER 1.0 - Initial global data cache
=========================================
VER 1.1 - Catalog Freshness + Roster Helpers
- Date: May 8, 2026
- FEATURE: fetchRoster and fetchCatalog extracted as named async functions
  so they can be called independently for targeted refreshes.
- FEATURE: subscribes to tecc_templates changes via RealtimeContext so the
  catalog stays fresh if templates are added/edited while the app is running.
=========================================
VER 1.4 - 2026-10-12 - BUILD-2026.1067 - RMI-2026-707: techRoster now loads is_available_for_dispatch (and profile_type);
  new refreshRoster() reloads just the roster after a profile change.
VER 1.3 - 2026-08-17 - RMI-2026-485: exclude deactivated staff from techRoster
- Surfaced by RMI-2026-484's new Command Inbox staff DM picker: a
  deactivated account (is_active: false) still showed up, because this
  query only filtered status='ACTIVE' and never checked is_active --
  profiles_v2 has both, and WorkOrderCreationWizard/WorkOrderEditor's own
  (separate, locally-fetched) tech pickers already filter on both. Added
  .eq('is_active', true) here so every techRoster consumer (WO tech-assign
  pickers via WorkOrderPreviewPanel, Command Inbox's staff DM picker)
  benefits, not just the one that surfaced it.
=========================================
VER 1.2 - May 14, 2026 — LOGIN LATENCY REDUCTION
- PERF: userTier now derives from useAuth().profile.tier_level instead of
  running a fresh supabase.auth.getUser() + profiles_v2 SELECT on mount.
  AuthContext already loaded this exact value. Eliminates two network
  round-trips from the post-login render path.
- DEPS: This file now requires AuthProvider to be a parent in the tree.
  In App.jsx it is — AuthProvider wraps AppRouter which renders
  InternalApp (which contains AppDataProvider).
=========================================
*/
/* [ END OF MODULE - VER 1.4 (src/context/AppDataContext.jsx) ] */
